"use client";
import { useState, useRef } from "react";
import { authServices } from "@/app/services/authServices";
import { useRouter } from "next/navigation";
import { TurnstileInstance } from "@marsidev/react-turnstile";
import toast from "react-hot-toast";

const useLogin = () => {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [token, setToken] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";
  const turnstileRef = useRef<TurnstileInstance>(null);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!turnstileToken) {
      setError("Harap selesaikan verifikasi captcha");
      setLoading(false);
      return;
    }

    try {
      const result = await authServices.login({
        email,
        password,
        captchaToken: turnstileToken,
      });

      if (!result.success) {
        setError(result.message || "Login gagal");
        turnstileRef.current?.reset();
        setTurnstileToken("");
        return;
      }

      if (result.token) {
        localStorage.setItem("token", result.token);
        window.dispatchEvent(new Event("auth-login"));
      }

      if (result.must_change_password) {
        setMustChangePassword(true);
        toast("Silakan ganti password Anda terlebih dahulu.", {
          icon: "⚠️",
        });
        router.push("/pages/change-password");
        return; // ⬅️ dipisah dari router.push, lebih jelas & konsisten dgn early-return lain
      }

      toast.success("Login berhasil. Mengalihkan...");
      router.push("/pages/menu-utama/dashboard");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const res = await authServices.resetRequest({
        identifier,
        captchaToken: turnstileToken,
      });

      if (!res.success) {
        setError(res.message || "Reset Password gagal");
        turnstileRef.current?.reset();
        setTurnstileToken("");
        return;
      }

      toast.success("Token telah dikirim ke email Anda");
      setSuccessMessage(
        "Token telah dikirim ke email Anda. Mengalihkan ke halaman reset..."
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Terjadi kesalahan, coba lagi"
      );
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    error,
    successMessage,
    showPassword,
    togglePasswordVisibility,
    turnstileToken,
    setTurnstileToken,
    turnstileRef,
    siteKey,
    handleLogin,
    handleResetRequest,
    identifier,
    setIdentifier,
    token,
    setToken,
    mustChangePassword,
    setMustChangePassword,
  };
};

export default useLogin;